import { cn } from '@/lib/utils'
import type { BudgetAlert } from '@/api/metrics'

interface BudgetMonitorProps {
  alerts: BudgetAlert[]
  loading?: boolean
}

const alertColors = {
  ok: { bg: 'bg-emerald-100', text: 'text-emerald-800', bar: 'bg-emerald-500' },
  warning: { bg: 'bg-amber-100', text: 'text-amber-800', bar: 'bg-amber-500' },
  critical: { bg: 'bg-red-100', text: 'text-red-800', bar: 'bg-red-500' },
}

export default function BudgetMonitor({ alerts, loading = false }: BudgetMonitorProps) {
  if (loading) {
    return (
      <div className="rounded-lg border border-[var(--border)] bg-[var(--card)] p-4">
        <h3 className="text-sm font-semibold mb-3">Budget-Monitor</h3>
        <div className="h-[200px] flex items-center justify-center text-sm text-[var(--muted-foreground)]">Lade...</div>
      </div>
    )
  }

  // Sort: critical first, then warning, then ok
  const sortOrder = { critical: 0, warning: 1, ok: 2 }
  const sorted = [...alerts].sort((a, b) => sortOrder[a.alertLevel] - sortOrder[b.alertLevel])

  const criticalCount = alerts.filter(a => a.alertLevel === 'critical').length
  const warningCount = alerts.filter(a => a.alertLevel === 'warning').length

  return (
    <div className="rounded-lg border border-[var(--border)] bg-[var(--card)] p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold">Budget-Monitor</h3>
        <div className="flex gap-2 text-xs">
          {criticalCount > 0 && (
            <span className="rounded-full bg-red-100 px-2 py-0.5 text-red-800 font-medium">{criticalCount} kritisch</span>
          )}
          {warningCount > 0 && (
            <span className="rounded-full bg-amber-100 px-2 py-0.5 text-amber-800 font-medium">{warningCount} Warnung</span>
          )}
        </div>
      </div>

      {sorted.length === 0 ? (
        <p className="text-sm text-[var(--muted-foreground)]">Keine Kampagnen mit Budget gefunden</p>
      ) : (
        <div className="space-y-2.5 max-h-[300px] overflow-y-auto">
          {sorted.map(alert => {
            const colors = alertColors[alert.alertLevel]
            return (
              <div key={alert.campaignId} className="space-y-1">
                <div className="flex items-center justify-between text-xs">
                  <span className="truncate max-w-[160px] font-medium" title={alert.name}>{alert.name}</span>
                  <span className={cn('rounded-full px-1.5 py-0.5 font-medium', colors.bg, colors.text)}>
                    {alert.utilization.toFixed(0)}%
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex-1 h-1.5 rounded-full bg-[var(--muted)]">
                    <div
                      className={cn('h-full rounded-full transition-all', colors.bar)}
                      style={{ width: `${Math.min(100, alert.utilization)}%` }}
                    />
                  </div>
                  <span className="text-[10px] text-[var(--muted-foreground)] whitespace-nowrap">
                    {alert.avgDailySpend7d.toFixed(2)} / {alert.dailyBudget.toFixed(2)}
                  </span>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
